import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import Seo from '../components/Seo';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import { Clock, ArrowRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const BlogIndex = () => {
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('All');
  const [bookOpen, setBookOpen] = useState(false);

  const tags = useMemo(() => ['All', ...Array.from(new Set(blogPosts.map((p) => p.tag)))], []);
  const filtered = useMemo(
    () =>
      blogPosts.filter((p) => {
        const matchesTag = tag === 'All' || p.tag === tag;
        const q = query.toLowerCase().trim();
        const matchesQuery = !q || p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
        return matchesTag && matchesQuery;
      }),
    [query, tag]
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'NutrifyMe Journal',
    description: 'Nutrition, hormones, gut health and sustainable weight loss insights by Dietitian Prachi.',
    blogPost: blogPosts.slice(0, 10).map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.description,
      datePublished: p.date,
      image: p.image,
      url: `/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <Seo
        title="Nutrition Journal - PCOS, Weight Loss, Gut Health"
        description="Evidence-based Indian nutrition articles by Dietitian Prachi on PCOS, weight loss, gut health, thyroid, pregnancy nutrition and sustainable habits."
        keywords={['nutrition blog india', 'pcos diet blog', 'weight loss india', 'dietitian prachi', 'indian nutrition articles']}
        path="/blog"
        jsonLd={jsonLd}
      />
      <Navbar onBook={() => setBookOpen(true)} />

      <section className="pt-32 pb-14 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#e8f2e2] opacity-60 blur-3xl" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-xs tracking-[0.2em] uppercase text-[#c9743a] mb-3">Journal</div>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#1a2b17] leading-[1.05]">
            Nutrition <span className="italic text-[#c9743a] font-light">wisdom</span>, no fluff.
          </h1>
          <p className="mt-5 text-lg text-[#4a5544] max-w-2xl">
            Real answers on PCOS, gut health, weight loss and Indian nutrition - written by dietitians, not influencers.
          </p>

          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#7a8471]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-11 h-12 rounded-full bg-white border-neutral-200"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTag(t)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition ${
                    tag === t ? 'bg-[#1a2b17] text-white border-[#1a2b17]' : 'bg-white text-[#1a2b17] border-neutral-200 hover:border-[#1a2b17]'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-[#7a8471]">No articles match. Try clearing the filters.</div>
          )}
          {filtered.map((p) => (
            <Link to={`/blog/${p.slug}`} key={p.slug} className="group bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3 text-xs">
                  <span className="tracking-widest uppercase text-[#c9743a] bg-[#fce7d5] px-2.5 py-1 rounded-full">{p.tag}</span>
                  <span className="inline-flex items-center gap-1 text-[#7a8471]"><Clock className="w-3 h-3" /> {p.readMins} min</span>
                </div>
                <h2 className="font-serif text-xl text-[#1a2b17] leading-snug group-hover:text-[#3a5a2c]">{p.title}</h2>
                <p className="text-sm text-[#4a5544] mt-3 leading-relaxed line-clamp-3">{p.description}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#1a2b17] group-hover:text-[#c9743a]">
                  Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer onBook={() => setBookOpen(true)} />
      <BookingModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
  );
};

export default BlogIndex;
